import { apiFetch } from './client';

// Server: progression/AbilityStateDto.java + ProgressionController's /training/abilities.
// `state` mirrors AbilitySlotService.learnable's own three-way classification exactly — the
// client never re-derives it (level gate, already-known) from levelReq/companion level itself,
// same "server is the source of truth for what's affordable/allowed" rule ShopScreen's own
// catalog rows follow.
export type AbilityState = 'learnable' | 'level_locked' | 'already_known';

export interface LearnableAbility {
  code: string;
  name: string;
  element: string;
  levelReq: number;
  cost: number;
  state: AbilityState;
  /** Everything below is content-authored, read straight off the `abilities` row server-side, and
   *  exists for the info sheet (SCREENS.md "/world/training — Abilities tab" → `info`). The client
   *  formats these but never derives or hardcodes them, so a new ability needs no web change.
   *  Optional so that any fixture or cached payload predating this field still type-checks — the
   *  sheet omits a line it has no value for rather than rendering "undefined". */
  description?: string;
  /** Attack-stat family: `physical` (power), `magical` (focus), `support` (no damage roll).
   *  Deliberately not the same axis as `status`. */
  kind?: string;
  tempo?: string;
  manaCost?: number;
  /** `0` for every support ability — key "does this deal damage?" off this, not off `kind`. */
  powerMult?: number;
  /** The status this ability applies, or absent when it applies none. */
  status?: string;
}

// Server: progression/AbilitySlotDto.java. Always six entries, `slotIndex` 0-5, matching the
// six unlock levels (1/5/10/15/20/30) 1-for-1 with the six-ability element ladder.
export interface AbilitySlot {
  slotIndex: number;
  abilityCode: string | null;
  unlocked: boolean;
  unlocksAtLevel: number;
}

export const fetchLearnable = (companionId: number, element: string) =>
  apiFetch<{ abilities: LearnableAbility[] }>(
    `/training/abilities?element=${element}&companionId=${companionId}`);

export const fetchSlots = (companionId: number) =>
  apiFetch<{ slots: AbilitySlot[]; unlockedSlots: number }>(
    `/companions/${companionId}/abilities`);

// POST /api/v1/training, the ability-lesson branch (ProgressionController: exactly one of
// `stat` and `abilityCode` must be present — this caller only ever sends the abilityCode pair).
//
// Final whole-branch review fix wave, I-1: the request type's field is `notify`, not
// `notifyOnComplete` — the server's `TrainingRequest` record binds the wire key `"notify"`
// (`@JsonProperty("notify") boolean notifyOnComplete`; the Java name is renamed only because a
// record component literally named `notify` collides with `java.lang.Object.notify()`, same
// reasoning as `TrainingSessionDto`). This function's `body` is passed straight to
// `JSON.stringify`, so whatever key is used here becomes the literal JSON key on the wire — a
// caller sending `notifyOnComplete` produces an unknown key Jackson silently ignores, and the
// flag defaults to `false`. `api/training.ts`'s `startTraining` already sends `notify` correctly;
// this was the one caller that didn't.
export const startLesson = (body: {
  companionId: number; abilityCode: string; slotIndex: number;
  notify: boolean; idempotencyKey: string;
}) => apiFetch<unknown>('/training', { method: 'POST', body: JSON.stringify(body) });
